import React from "react";
import { WorkflowRun } from "../../../services/WorkflowService";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Button } from "../../ui/button";
import { HistoryIcon } from "lucide-react";
import moment from "moment";

interface WorkflowSessionsTabProps {
  runs: WorkflowRun[];
  runsTotal: number;
  runsPage: number;
  setRunsPage: React.Dispatch<React.SetStateAction<number>>;
  runsLoading: boolean;
  onSelectRun: (run: WorkflowRun) => void;
}

export const SESSIONS_PAGE_SIZE = 10;

const getStatusClasses = (status: string) => {
  const base = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border";
  switch (status?.toUpperCase()) {
    case "COMPLETED":
      return `${base} border-green-500 text-green-700 dark:text-white bg-card [background-image:linear-gradient(rgba(34,197,94,0.1),rgba(34,197,94,0.1))] dark:[background-image:linear-gradient(rgba(34,197,94,0.2),rgba(34,197,94,0.2))]`;
    case "FAILED":
      return `${base} border-red-500 text-red-700 dark:text-white bg-card [background-image:linear-gradient(rgba(239,68,68,0.1),rgba(239,68,68,0.1))] dark:[background-image:linear-gradient(rgba(239,68,68,0.2),rgba(239,68,68,0.2))]`;
    case "RUNNING":
      return `${base} border-blue-500 text-blue-700 dark:text-white bg-card [background-image:linear-gradient(rgba(59,130,246,0.1),rgba(59,130,246,0.1))] dark:[background-image:linear-gradient(rgba(59,130,246,0.2),rgba(59,130,246,0.2))]`;
    case "TIMEOUT":
      return `${base} border-orange-500 text-orange-700 dark:text-white bg-card [background-image:linear-gradient(rgba(251,146,60,0.1),rgba(251,146,60,0.1))] dark:[background-image:linear-gradient(rgba(251,146,60,0.2),rgba(251,146,60,0.2))]`;
    default:
      return `${base} border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-card`;
  }
};

const formatDuration = (run: WorkflowRun) => {
  const profile = run.profile as any;
  if (!profile) return "—";
  const spans = profile?.spans as any[];
  const workerExec = spans?.find((s: any) => s.label === "worker_execution");
  if (workerExec?.start_ms != null && workerExec?.end_ms != null) {
    const ms = Math.round(workerExec.end_ms - workerExec.start_ms);
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }
  if (profile?.total_ms != null && Number(profile.total_ms) > 0) {
    const ms = Math.round(Number(profile.total_ms));
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }
  return "—";
};

const WorkflowSessionsTab: React.FC<WorkflowSessionsTabProps> = ({
  runs,
  runsTotal,
  runsPage,
  setRunsPage,
  runsLoading,
  onSelectRun,
}) => {
  const totalPages = Math.ceil(runsTotal / SESSIONS_PAGE_SIZE);

  if (runsLoading) {
    return (
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border hover:bg-transparent">
            <TableHead className="w-[180px]">Session ID</TableHead>
            <TableHead className="w-[180px]">Created</TableHead>
            <TableHead className="w-[120px]">Status</TableHead>
            <TableHead className="w-[100px]">Duration</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i} className="border-b border-border/50">
              <TableCell className="py-4">
                <div className="h-4 w-24 bg-muted animate-pulse rounded" />
              </TableCell>
              <TableCell className="py-4">
                <div className="h-4 w-32 bg-muted animate-pulse rounded" />
              </TableCell>
              <TableCell className="py-4">
                <div className="h-6 w-24 bg-muted animate-pulse rounded-full" />
              </TableCell>
              <TableCell className="py-4">
                <div className="h-4 w-12 bg-muted animate-pulse rounded" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="space-y-3">
          <HistoryIcon className="w-16 h-16 text-muted-foreground mx-auto" />
          <h3 className="text-lg font-gc-med text-foreground">No sessions found</h3>
          <p className="text-muted-foreground max-w-md">
            Workflow sessions will appear here when the workflow is executed via the API.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border hover:bg-transparent">
            <TableHead className="w-[180px]">Session ID</TableHead>
            <TableHead className="w-[180px]">Created</TableHead>
            <TableHead className="w-[120px]">Status</TableHead>
            <TableHead className="w-[100px]">Duration</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map((run) => (
            <TableRow
              key={run.id}
              className="border-b border-border/50 cursor-pointer hover:bg-accent/50"
              onClick={() => onSelectRun(run)}
            >
              <TableCell className="py-4">
                <span className="font-mono text-xs">
                  {run.session_id.substring(0, 8)}...
                </span>
              </TableCell>
              <TableCell className="py-4">
                {run.date_created
                  ? moment(run.date_created).format("MMM D, h:mm:ss A")
                  : "—"}
              </TableCell>
              <TableCell className="py-4">
                <span className={getStatusClasses(run.status)}>
                  {run.status}
                </span>
              </TableCell>
              <TableCell className="py-4">
                <span className="text-sm text-muted-foreground">
                  {formatDuration(run)}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between px-2 py-4">
        <div className="text-sm text-muted-foreground">
          Showing {runsPage * SESSIONS_PAGE_SIZE + 1} to{" "}
          {Math.min((runsPage + 1) * SESSIONS_PAGE_SIZE, runsTotal)} of {runsTotal} sessions
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRunsPage((p) => p - 1)}
            disabled={runsPage === 0}
          >
            Previous
          </Button>
          <span className="text-sm">
            Page {runsPage + 1} of {totalPages || 1}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRunsPage((p) => p + 1)}
            disabled={runsPage + 1 >= totalPages}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
};

export default WorkflowSessionsTab;
